<template>
  <div class="na-page na-staff">
    <StaffHeader :role="viewerRole" />

    <main class="st-wrap st-below">
      <div class="st-head">
        <div>
          <span class="st-kicker">Builds</span>
          <h1 class="st-title">App <em>progress</em></h1>
          <p class="st-sub">Move each customer through their build. Every change is saved by the server, logged, and — if you leave it ticked — emailed to the customer with your note.</p>
        </div>
      </div>

      <!-- Board -->
      <div class="board">
        <button v-for="s in STAGES" :key="s" type="button" class="col" :class="{ on: filterStage === s }" @click="filterStage = filterStage === s ? '' : s">
          <b>{{ counts[s] || 0 }}</b><span>{{ s }}</span>
        </button>
      </div>

      <div class="layout">
        <!-- List -->
        <section class="st-card list">
          <label class="st-search"><v-icon size="18">mdi-magnify</v-icon><input v-model="search" placeholder="Search customer or email" /></label>
          <div v-if="loading" class="state"><div class="st-loader"></div></div>
          <div v-else-if="!filtered.length" class="st-empty"><p>{{ customers.length ? 'No customers match.' : 'No customers yet. Onboard one to get started.' }}</p></div>
          <ul v-else>
            <li v-for="c in filtered" :key="c.id" :class="{ on: c.id === selectedId }" @click="select(c.id)">
              <span class="st-avatar" :style="c.logoURL ? { backgroundImage: `url(${c.logoURL})` } : {}">{{ c.logoURL ? '' : initials(c.businessName) }}</span>
              <span class="li-main"><b>{{ c.businessName || '(No name)' }}</b><small>{{ c.ownerEmail }}</small></span>
              <span class="st-badge" :class="stageTone(c.appProgress)">{{ normalizeStage(c.appProgress) }}</span>
            </li>
          </ul>
        </section>

        <!-- Selected -->
        <section class="st-card detail">
          <div v-if="!selected" class="st-empty"><v-icon size="40">mdi-cursor-default-click-outline</v-icon><h3>Pick a customer</h3><p>Their build journey and history appear here.</p></div>
          <template v-else>
            <div class="st-card-head">
              <div>
                <h2 class="st-card-title">{{ selected.businessName }}</h2>
                <span class="st-muted">{{ selected.planName || 'Community' }} plan · {{ subStatus(selected.subscriptionStatus).label }} · {{ selected.ownerEmail }}</span>
              </div>
              <router-link :to="`/admin/customers/${selected.id}`" class="st-btn outline sm">Open customer</router-link>
            </div>

            <div class="st-journey">
              <div v-for="(s, i) in STAGES" :key="s" class="st-step" :class="{ done: i <= curIdx, current: i === curIdx }">
                <span class="st-dot"><v-icon v-if="i < curIdx" size="16" color="#fff">mdi-check</v-icon><template v-else>{{ i + 1 }}</template></span>
                <b>{{ s }}</b><small>{{ dateFor(s) }}</small>
              </div>
            </div>

            <div class="st-stack form">
              <div class="st-field">
                <label>New stage</label>
                <div class="st-chips">
                  <button v-for="s in STAGES" :key="s" type="button" class="st-chip" :class="{ on: newStage === s }" :title="STAGE_HELP[s]" @click="newStage = s">{{ s }}</button>
                </div>
                <small class="st-muted">{{ STAGE_HELP[newStage] }}<template v-if="next && newStage === cur"> Next up: {{ next }}.</template></small>
              </div>
              <div class="st-field">
                <label>Note to the customer (optional)</label>
                <textarea v-model="note" rows="3" maxlength="1500" placeholder="Anything they should know — what you need from them, dates, what's next."></textarea>
              </div>
              <label class="notify"><input v-model="notify" type="checkbox" /> Email {{ selected.ownerEmail }} about this change</label>
              <button type="button" class="st-btn primary" :disabled="saving || newStage === cur" @click="save">{{ saving ? 'Saving…' : newStage === cur ? `Already at ${cur}` : `Move to ${newStage}` }}</button>
              <div v-if="error" class="st-alert">{{ error }}</div>
            </div>

            <div class="st-divider">History</div>
            <div v-if="!history.length" class="st-muted">No stage changes logged yet.</div>
            <ul v-else class="history">
              <li v-for="h in history" :key="h.id">
                <b>{{ h.from }} → {{ h.to }}</b>
                <span class="st-muted">{{ fmtDate(h.at, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) }} · {{ h.byEmail || 'staff' }}{{ h.notified ? ' · emailed' : ' · not emailed' }}</span>
                <p v-if="h.note">{{ h.note }}</p>
              </li>
            </ul>
          </template>
        </section>
      </div>
    </main>

    <div v-if="toast" class="st-toast">{{ toast }}<button type="button" @click="toast = ''">✕</button></div>
  </div>
</template>

<script>
import StaffHeader from '@/components/StaffHeader.vue'
import { auth, db, functions } from '@/firebase'
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { STAGES, STAGE_HELP, normalizeStage, stageIndex, subStatus, fmtDate, fnError } from '@/billingConfig'
import '@/assets/na-staff.css'

export default {
  name: 'ManageAppProgressView',
  components: { StaffHeader },
  data: () => ({
    STAGES, STAGE_HELP, viewerRole: 'Staff',
    customers: [], loading: true, search: '', filterStage: '', selectedId: null,
    newStage: 'Not Started', note: '', notify: true, saving: false, error: '', history: [], toast: '',
    unsub: null, unsubHistory: null,
  }),
  computed: {
    counts() { return this.customers.reduce((m, c) => { const s = normalizeStage(c.appProgress); m[s] = (m[s] || 0) + 1; return m }, {}) },
    filtered() {
      const q = this.search.trim().toLowerCase()
      return this.customers
        .filter(c => !this.filterStage || normalizeStage(c.appProgress) === this.filterStage)
        .filter(c => !q || [c.businessName, c.ownerEmail, c.publicationName].some(v => String(v || '').toLowerCase().includes(q)))
    },
    selected() { return this.customers.find(c => c.id === this.selectedId) || null },
    cur() { return normalizeStage(this.selected && this.selected.appProgress) },
    curIdx() { return stageIndex(this.cur) },
    next() { return STAGES[this.curIdx + 1] || '' },
  },
  async mounted() {
    const u = auth.currentUser
    if (u) { try { const s = await getDoc(doc(db, 'users', u.uid)); if (s.exists() && s.data().role === 'Admin') this.viewerRole = 'Admin' } catch (e) { /* default */ } }
    this.unsub = onSnapshot(collection(db, 'customers'), s => {
      this.customers = s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => stageIndex(a.appProgress) - stageIndex(b.appProgress) || String(a.businessName || '').localeCompare(String(b.businessName || '')))
      this.loading = false
      const want = this.$route.query.customer
      if (want && !this.selectedId && this.customers.some(c => c.id === want)) this.select(String(want))
    }, e => { console.error(e); this.loading = false })
  },
  beforeDestroy() { if (this.unsub) this.unsub(); if (this.unsubHistory) this.unsubHistory() },
  methods: {
    normalizeStage, subStatus, fmtDate,
    initials(n) { return String(n || 'NA').split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase() },
    stageTone(s) { const i = stageIndex(s); return i === STAGES.length - 1 ? 'green' : i === 0 ? '' : i >= 3 ? 'brass' : 'navy' },
    dateFor(s) { const d = this.selected.appProgressDates || {}; return d[s] ? fmtDate(d[s], { month: 'short', day: 'numeric' }) : '' },
    select(id) {
      this.selectedId = id
      this.error = ''
      this.note = ''
      this.notify = true
      const c = this.customers.find(x => x.id === id)
      this.newStage = STAGES[Math.min(STAGES.length - 1, stageIndex(c && c.appProgress) + 1)]
      if (this.unsubHistory) this.unsubHistory()
      this.history = []
      this.unsubHistory = onSnapshot(query(collection(db, 'customers', id, 'progress_history'), orderBy('at', 'desc'), limit(25)), s => { this.history = s.docs.map(d => ({ id: d.id, ...d.data() })) }, e => console.warn(e))
      if (this.$route.query.customer !== id) this.$router.replace({ query: { customer: id } }).catch(() => {})
    },
    async save() {
      this.saving = true
      this.error = ''
      try {
        await httpsCallable(functions, 'setAppProgress')({ companyId: this.selectedId, stage: this.newStage, note: this.note.trim(), notify: this.notify })
        this.toast = this.notify ? `Moved to ${this.newStage}. ${this.selected.ownerEmail} has been emailed.` : `Moved to ${this.newStage}.`
        setTimeout(() => { this.toast = '' }, 3500)
        this.note = ''
      } catch (e) { this.error = 'Could not update: ' + fnError(e) } finally { this.saving = false }
    },
  },
}
</script>

<style scoped>
.board { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 24px; }
.board .col { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 14px 16px; border: 1px solid var(--na-rule); border-top: 3px solid var(--na-navy); border-radius: 10px; background: var(--na-card); cursor: pointer; font-family: var(--na-sans); text-align: left; }
.board .col:last-child { border-top-color: #2F7D46; }
.board .col.on { background: #F2F5FA; box-shadow: 0 0 0 2px var(--na-navy); }
.board b { font-family: var(--na-serif); font-size: 1.7rem; color: var(--na-ink); }
.board span { font-size: 0.82rem; color: var(--na-ink-2); }
.layout { display: grid; grid-template-columns: 360px minmax(0, 1fr); gap: 24px; align-items: start; }
.list { display: flex; flex-direction: column; gap: 14px; position: sticky; top: 96px; max-height: calc(100vh - 120px); }
.list ul { list-style: none; margin: 0; padding: 0; overflow-y: auto; }
.list li { display: flex; align-items: center; gap: 10px; padding: 10px; border-radius: 8px; cursor: pointer; border: 1px solid transparent; }
.list li:hover { background: #FBF8F1; }
.list li.on { background: #F2F5FA; border-color: var(--na-navy); }
.list .st-avatar { width: 36px; height: 36px; font-size: 0.8rem; }
.li-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.li-main small { color: var(--na-ink-2); font-size: 0.82rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.state { display: flex; justify-content: center; padding: 30px; }
.detail { display: flex; flex-direction: column; gap: 20px; }
.form { padding-top: 6px; }
.notify { display: flex; align-items: center; gap: 8px; font-size: 0.93rem; }
.notify input { width: 17px; height: 17px; accent-color: var(--na-navy); }
.history { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.history li { display: flex; flex-direction: column; gap: 2px; padding: 10px 14px; background: #FAF6EE; border-radius: 8px; }
.history p { margin: 4px 0 0; color: var(--na-ink-2); white-space: pre-wrap; }
@media (max-width: 1000px) {
  .board { grid-template-columns: repeat(3, 1fr); }
  .layout { grid-template-columns: 1fr; }
  .list { position: static; max-height: 360px; }
}
</style>